import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-blue-100 hover:text-white hover:bg-blue-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-blue-100">Last Updated: December 6, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="prose prose-sm max-w-none pt-6">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                <strong>SRAGCircle OPC</strong>, through its portfolio brand <strong>LexDoRight</strong> ("we," "us," or "our"), operates LexProcure PH. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you use our Service.
              </p>
              <p className="text-gray-700 mb-4">
                We are committed to protecting your privacy and complying with the Data Privacy Act of 2012 (Republic Act No. 10173) 
                and other applicable Philippine data protection laws.
              </p>
              <p className="text-gray-700">
                Please read this Privacy Policy carefully. By using LexProcure PH, you consent to the data practices described herein.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">We collect the following personal information:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Account Information:</strong> Email address, full name, password (encrypted)</li>
                <li><strong>Payment Information:</strong> Payment method, transaction references (we do not store credit card details)</li>
                <li><strong>Profile Information:</strong> Any additional information you provide in your profile</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.2 Usage Data</h3>
              <p className="text-gray-700 mb-4">We automatically collect:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Search queries and browsing history within the Service</li>
                <li>Bookmarks, notes, and highlights you create</li>
                <li>Features and tools you use</li>
                <li>Date and time of access</li>
                <li>Device information (browser type, operating system)</li>
                <li>IP address and general location data</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.3 Cookies and Tracking</h3>
              <p className="text-gray-700">
                We use cookies and similar tracking technologies to track activity and store certain information. 
                You can instruct your browser to refuse cookies, but some features may not function properly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use collected information for:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Service Provision:</strong> To create and manage your account, process subscriptions, and provide access</li>
                <li><strong>Personalization:</strong> To save your bookmarks, notes, search history, and preferences</li>
                <li><strong>Communication:</strong> To send account notifications, trial expiry warnings, payment confirmations, and service updates</li>
                <li><strong>Improvement:</strong> To analyze usage patterns and improve the Service</li>
                <li><strong>Security:</strong> To detect fraud, prevent abuse, and protect user accounts</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our Terms</li>
                <li><strong>Customer Support:</strong> To respond to inquiries and provide assistance</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Legal Basis for Processing (Data Privacy Act Compliance)</h2>
              <p className="text-gray-700 mb-4">
                Under the Philippine Data Privacy Act, we process your personal data based on:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Consent:</strong> You explicitly consent when creating an account and using the Service</li>
                <li><strong>Contract:</strong> Processing is necessary to fulfill our subscription agreement with you</li>
                <li><strong>Legitimate Interest:</strong> We have legitimate interests in improving the Service and preventing fraud</li>
                <li><strong>Legal Obligation:</strong> We must process certain data to comply with legal requirements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Information Sharing and Disclosure</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 We DO NOT Sell Your Data</h3>
              <p className="text-gray-700 mb-4 font-semibold">
                We do not sell, trade, or rent your personal information to third parties.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with trusted third-party service providers who assist us in operating the Service:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Payment verification - for processing manual payments</li>
                <li>Hosting providers - for data storage and service infrastructure</li>
                <li>Email service providers - for sending notifications</li>
                <li>Analytics providers - for understanding usage patterns</li>
              </ul>
              <p className="text-gray-700 mb-4">
                These providers are contractually bound to protect your data and use it only for specified purposes.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.3 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose your information if required by law or in response to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Court orders or legal processes</li>
                <li>Government or regulatory requests</li>
                <li>Protection of our rights, property, or safety</li>
                <li>Prevention of fraud or illegal activity</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.4 Business Transfers</h3>
              <p className="text-gray-700">
                If we are involved in a merger, acquisition, or sale of assets, your information may be transferred. 
                We will notify you before your information becomes subject to a different privacy policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational security measures to protect your personal data:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Encryption of data in transit (HTTPS/SSL)</li>
                <li>Encrypted password storage (bcrypt hashing)</li>
                <li>Secure database access controls</li>
                <li>Regular security assessments</li>
                <li>Access restrictions to personal data</li>
                <li>Monitoring for unauthorized access</li>
              </ul>
              <p className="text-gray-700">
                However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, 
                we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Your Data Rights (Under Philippine Data Privacy Act)</h2>
              <p className="text-gray-700 mb-4">You have the following rights regarding your personal data:</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.1 Right to Access</h3>
              <p className="text-gray-700 mb-4">
                You can request a copy of your personal data we hold.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.2 Right to Rectification</h3>
              <p className="text-gray-700 mb-4">
                You can update or correct inaccurate personal data through your account settings.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.3 Right to Erasure</h3>
              <p className="text-gray-700 mb-4">
                You can request deletion of your personal data, subject to legal retention requirements.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.4 Right to Object</h3>
              <p className="text-gray-700 mb-4">
                You can object to certain processing of your data, such as direct marketing.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.5 Right to Data Portability</h3>
              <p className="text-gray-700 mb-4">
                You can request your data in a structured, commonly used format.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.6 Right to Withdraw Consent</h3>
              <p className="text-gray-700 mb-4">
                You can withdraw consent for data processing at any time, though this may affect your ability to use the Service.
              </p>

              <p className="text-gray-700">
                To exercise these rights, contact us at <strong>admin@lexdoright.com</strong>. We will respond within 30 days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal data for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide the Service and maintain your account</li>
                <li>Comply with legal obligations (tax, accounting requirements)</li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              <p className="text-gray-700">
                After account deletion, we may retain certain data for up to 5 years as required by Philippine law for 
                accounting and legal purposes. Non-personally identifiable data may be retained indefinitely for analytics.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700">
                LexProcure PH is not intended for individuals under 18 years of age. We do not knowingly collect personal 
                information from children. If you believe we have collected data from a minor, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. International Data Transfers</h2>
              <p className="text-gray-700">
                Your data may be transferred to and processed in servers located outside the Philippines. We ensure appropriate 
                safeguards are in place to protect your data in accordance with Philippine data protection standards.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of material changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Posting the new Privacy Policy on this page</li>
                <li>Updating the "Last Updated" date</li>
                <li>Sending an email notification to registered users</li>
              </ul>
              <p className="text-gray-700">
                Your continued use of the Service after changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Contact Us & Data Protection Officer</h2>
              <p className="text-gray-700 mb-4">
                For questions, concerns, or to exercise your data rights, contact us at:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Company:</strong> SRAGCircle OPC (via LexDoRight)</p>
                <p className="text-gray-700"><strong>Email:</strong> admin@lexdoright.com</p>
                <p className="text-gray-700"><strong>Website:</strong> lexdoright.com</p>
                <p className="text-gray-700 mt-2"><strong>Data Privacy Concerns:</strong> admin@lexdoright.com</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">13. Complaints to National Privacy Commission</h2>
              <p className="text-gray-700 mb-4">
                If you believe your data privacy rights have been violated, you have the right to file a complaint with the 
                National Privacy Commission (NPC) of the Philippines:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>National Privacy Commission</strong></p>
                <p className="text-gray-700">5th Floor, Philippine International Convention Center (PICC)</p>
                <p className="text-gray-700">Vicente Sotto Street, Pasay City, Metro Manila 1300</p>
                <p className="text-gray-700"><strong>Email:</strong> info@privacy.gov.ph</p>
                <p className="text-gray-700"><strong>Website:</strong> www.privacy.gov.ph</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Your privacy is important to us.</strong> We are committed to protecting your personal data and 
                being transparent about our practices. If you have any questions, please don't hesitate to contact us.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
